import kotlin.js.Date

// Dates travel through the whole app as plain "yyyy-MM-dd" strings -- that is what Postgres `date`
// columns, PostgREST filters and <input type="date"> all speak, so there is never a conversion to
// get wrong. The browser's local timezone stands in for Europe/Prague (SPECIFIKACE.md ch. 7):
// everyone in this group uses a Czech device, so a timezone library would buy nothing.

/** Formats this date as "yyyy-MM-dd" in the browser's local timezone. */
fun Date.toIsoDate(): String = isoDate(getFullYear(), getMonth() + 1, getDate())

fun isoDate(year: Int, month: Int, day: Int): String =
    "$year-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"

/**
 * Parses "yyyy-MM-dd" into a local-midnight [Date], or null when the string is not a well-formed
 * date -- the date input hands us partial values while the user is still typing one.
 */
fun parseIsoDate(iso: String): Date? {
    val parts = iso.split("-")
    if (parts.size != 3) return null
    val year = parts[0].toIntOrNull() ?: return null
    val month = parts[1].toIntOrNull() ?: return null
    val day = parts[2].toIntOrNull() ?: return null
    return Date(year, month - 1, day)
}

fun todayIso(): String = Date().toIsoDate()

fun yesterdayIso(): String = shiftDaysIso(todayIso(), -1)

/**
 * [iso] moved by [days], negative going back. Built through the Date constructor rather than
 * millisecond arithmetic, so month ends, year ends and daylight-saving changes normalise
 * themselves -- adding 24h across the March changeover lands on 23:00 the day before.
 */
fun shiftDaysIso(iso: String, days: Int): String {
    val date = parseIsoDate(iso) ?: return iso
    return Date(date.getFullYear(), date.getMonth(), date.getDate() + days).toIsoDate()
}

/** UI text, hence Czech. Indexed by `Date.getDay()`, where 0 is Sunday. */
private val CZECH_DAY_NAMES =
    listOf("neděle", "pondělí", "úterý", "středa", "čtvrtek", "pátek", "sobota")

/** Czech weekday name for an ISO date; empty for a half-typed date, which callers simply hide. */
fun dayNameFor(iso: String): String {
    val date = parseIsoDate(iso) ?: return ""
    return CZECH_DAY_NAMES.getOrElse(date.getDay()) { "" }
}

/**
 * Monday of the week containing [iso]. Weeks start on Monday throughout the app (SPECIFIKACE.md
 * ch. 6.2), matching `date_trunc('week', ...)` behind `cv_completed_weeks`.
 */
fun startOfWeekIso(iso: String = todayIso()): String {
    val date = parseIsoDate(iso) ?: return iso
    val daysSinceMonday = (date.getDay() + 6) % 7 // getDay() is 0=Sunday..6=Saturday
    return Date(date.getFullYear(), date.getMonth(), date.getDate() - daysSinceMonday).toIsoDate()
}

/** Monday of the week before the one containing [iso]. */
fun startOfPreviousWeekIso(iso: String = todayIso()): String = shiftDaysIso(startOfWeekIso(iso), -7)

/** Sunday closing the week that starts on [weekStart]. */
fun endOfWeekIso(weekStart: String): String = shiftDaysIso(weekStart, 6)

/** First day of the calendar month containing [iso]. */
fun startOfMonthIso(iso: String = todayIso()): String {
    val date = parseIsoDate(iso) ?: return iso
    return isoDate(date.getFullYear(), date.getMonth() + 1, 1)
}

/** First day of the month before the one containing [iso]. */
fun startOfPreviousMonthIso(iso: String = todayIso()): String {
    val date = parseIsoDate(iso) ?: return iso
    return Date(date.getFullYear(), date.getMonth() - 1, 1).toIsoDate()
}

/** Last day of the month that starts on [monthStart]. */
fun endOfMonthIso(monthStart: String): String {
    val date = parseIsoDate(monthStart) ?: return monthStart
    // Day 0 of the next month is the last day of this one -- the constructor normalises it.
    return Date(date.getFullYear(), date.getMonth() + 1, 0).toIsoDate()
}

/** Czech short form for display, e.g. "31. 8. 2026". */
fun formatCzechDate(iso: String): String {
    val date = parseIsoDate(iso) ?: return iso
    return "${date.getDate()}. ${date.getMonth() + 1}. ${date.getFullYear()}"
}

/** Czech date range for a leaderboard heading, e.g. "31. 8. 2026 - 6. 9. 2026". */
fun formatCzechDateRange(fromIso: String, toIso: String): String =
    if (fromIso == toIso) formatCzechDate(fromIso) else "${formatCzechDate(fromIso)} – ${formatCzechDate(toIso)}"

/** Every ISO date from [fromIso] to [toIsoInclusive], both ends included -- the statistics trend
 * chart's day buckets. */
fun datesBetween(fromIso: String, toIsoInclusive: String): List<String> {
    val result = mutableListOf<String>()
    var date = fromIso
    while (date <= toIsoInclusive) {
        result += date
        date = shiftDaysIso(date, 1)
    }
    return result
}

/** Two-letter weekday abbreviation, indexed like [CZECH_DAY_NAMES]. */
private val CZECH_DAY_SHORT = listOf("Ne", "Po", "Út", "St", "Čt", "Pá", "So")

fun shortDayNameFor(iso: String): String {
    val date = parseIsoDate(iso) ?: return ""
    return CZECH_DAY_SHORT.getOrElse(date.getDay()) { "" }
}
